import { useEffect, useId, useRef } from 'react'
import { Icon } from '@iconify/react'
import './ChangelogDialog.css'

interface ChangelogDialogProps {
  open: boolean
  onClose: () => void
}

const CHANGELOG = [
  {
    version: '0.13.0',
    date: '14 AGO 2026',
    title: 'Versión 0.13.0 · El Mundial ya tiene su propio camino',
    notes: [
      'El mapa del Mundial separa fase de grupos, soporte, partidos únicos y descanso, con el descanso colocado antes de los tres últimos partidos.',
      'El ticker de liga no se reutiliza durante el Mundial: la fase de grupos muestra tres encuentros y los cruces eliminatorios se resuelven como partidos únicos.',
        'Las insignias federativas locales para las 48 selecciones del cuadro sustituyen a las banderas, y las rutas sudamericanas usan nombres de competición genéricos cuando no corresponde hablar de Europa.',
      'El diálogo de partidos en móvil envuelve el título y apila las acciones para mantener «Simular hasta el resultado» y «Continuar» siempre accesibles.',
    ],
  },
  {
    version: '0.12.2',
    date: '13 AGO 2026',
    title: 'Versión 0.12.2 · Una cabecera, toda la carrera',
    notes: [
      'El nombre de la etapa y el periodo pasan a la ficha compacta del jugador; desaparece la fila repetida del mapa y la misma información se mantiene en web, tableta y móvil.',
      'Los nodos disponibles se reconocen sólo por su color y borde: se elimina la etiqueta «Elige» para dejar el mapa más limpio.',
      'La cabecera sticky ocupa menos altura y la leyenda flota sobre el mapa sin empujar el recorrido hacia abajo.',
    ],
  },
  {
    version: '0.12.1',
    date: '13 AGO 2026',
    title: 'Versión 0.12.1 · Acciones siempre al alcance',
    notes: [
      'En móvil, la acción principal de boss, derbi y tramo de liga permanece visible mientras revisas la preparación, respetando el área segura inferior.',
      'El resultado del tramo en tableta conserva escudo, equipo, minutos, producción, resultado y marcador en una sola fila legible.',
    ],
  },
  {
    version: '0.12.0',
    date: '13 AGO 2026',
    title: 'Versión 0.12.0 · Todo lo que descubres cuenta',
    notes: [
      'El glosario empieza oculto y se completa al encontrar reliquias y consumibles: cada descubrimiento permanece entre runs y se sincroniza con tu cuenta.',
      'El relato ordena las acciones más recientes primero y destaca las intervenciones de tu jugador para que goles, asistencias y acciones clave se reconozcan al instante.',
      'Al terminar cada ronda de boss vuelve al inicio de la preparación, mantiene la acción principal accesible y enseña sólo la información necesaria para la siguiente final.',
      'El acceso con Google sólo aparece cuando el proveedor está disponible; si no está configurado, el correo sigue funcionando sin mostrar errores técnicos.',
      'El expediente final prioriza tu recorrido en móvil, agrupa goles y asistencias sin un total de títulos ambiguo y mejora la jerarquía de cada etapa.',
      'En móvil, el panel de Build limita su altura y desplaza el contenido internamente al abrir reliquias, mientras las decisiones y los resúmenes conservan filas legibles.',
      'La categoría de élite usa una selección explícita de grandes clubes para mantener coherentes el Mundial secreto, los traspasos y los rivales continentales.',
    ],
  },
  {
    version: '0.11.0',
    date: '13 AGO 2026',
    title: 'Versión 0.11.0 · La carrera se ve y se siente mejor',
    notes: [
      'Los tramos de liga pasan a cinco partidos y muestran los minutos realmente disputados, con un resumen individual más representativo de cada etapa.',
      'La producción individual se calcula por 90 minutos según posición, etapa y OVR: un buen delantero ya no depende de que el equipo marque primero para producir.',
      'Cada alternativa de evento enfrenta una recompensa con un sacrificio visible; incluso las salidas de riesgo bajo dejan de ser mejoras gratuitas.',
      'El mapa, las decisiones, el glosario, la build y el expediente final se reorganizan para aprovechar móvil, tablet, 1080p y 2K sin cortar textos ni desbordar tarjetas.',
      'Los destinos de carrera comparan el nivel del club y el prestigio de la liga de destino, y ajustan OVR, moral, reputación y riesgo al salto competitivo real.',
      'Las eliminatorias terminan cuando el éxito o el fracaso ya es matemático, el Triplete exige ganar las tres finales y los rivales continentales proceden de otros países.',
    ],
  },
  {
    version: '0.10.0',
    date: '13 AGO 2026',
    title: 'Versión 0.10.0 · Cada eliminatoria cuenta',
    notes: [
      'La etapa de Élite se juega a tres eliminatorias: una derrota en cuartos, semifinal o final termina el camino hacia el título.',
      'Los tramos decisivos terminan cuando el objetivo queda resuelto: un 2-0 o un 0-2 ya no obliga a disputar una tercera ronda irrelevante.',
      'El Triplete exige ganar sus tres finales: perder cualquiera termina la run y cancela las finales restantes.',
      'Según el nivel de tu club, disputarás la copa nacional o la competición continental; la dificultad base se ajusta sin facilitar la reacción de remontada.',
      'El prestigio ofrece más oportunidades reales de disputar cada partido y la progresión temprana mantiene equilibradas las rutas de MCO y DC, renovación y traspaso.',
      'Los goles y asistencias crecen con cada etapa: el MCO evoluciona como creador y el DC como finalizador, pero ambos producen en las dos facetas.',
      'Los traspasos combinan categoría del club y prestigio de la liga: un élite europeo, brasileño o saudí ya no concede automáticamente el mismo salto de OVR.',
      'La preparación oculta el selector cuando no tienes consumibles compatibles y el resultado de las series de boss se adapta mejor a las pantallas móviles.',
      'Un toque breve ya no abre por error la ayuda del nodo: la pulsación prolongada y el acceso mediante teclado conservan su comportamiento.',
    ],
  },
  {
    version: '0.9.0',
    date: '13 AGO 2026',
    title: 'Versión 0.9.0 · Tu tramo, tus números',
    notes: [
      'Los tramos de liga admiten victorias, empates y derrotas con el sistema 3-1-0, sin saltos artificiales al cambiar la probabilidad de ganar.',
      'Cada resultado indica si jugaste y tu producción; el resumen del tramo reúne partidos, minutos, goles y asistencias sin adelantar acciones futuras.',
      'El OVR y el prestigio dependen de lo que juegas y produces: una victoria lograda desde el banquillo ya no infla tu progresión individual.',
      'El informe final conserva tus apariciones y minutos por etapa junto a goles, asistencias, OVR y títulos, también al migrar partidas anteriores.',
      'Las descripciones mecánicas de objetos y reliquias quedan debajo de su título para mantener alineados nombre, condición y valor.',
    ],
  },
  {
    version: '0.8.3',
    date: '13 AGO 2026',
    title: 'Versión 0.8.3 · La remontada tiene nombre',
    notes: [
      'Las remontadas de boss y derbi atribuyen ahora el primer gol a un compañero real en lugar de mostrar «El equipo».',
      'El goleador se resuelve desde la plantilla del club incluso cuando el encuentro no recibe una alineación explícita.',
    ],
  },
  {
    version: '0.8.2',
    date: '13 AGO 2026',
    title: 'Versión 0.8.2 · Todas las opciones al alcance',
    notes: [
      'El selector de mejoras vuelve a desplazarse con el dedo en móvil y tableta aunque las tres opciones superen la altura disponible.',
      'La tercera opción queda siempre accesible: cada tarjeta permite deslizar verticalmente sin seleccionar por accidente y conserva el zoom táctil.',
    ],
  },
  {
    version: '0.8.1',
    date: '13 AGO 2026',
    title: 'Versión 0.8.1 · Italia vuelve al Mundial',
    notes: [
      'Italia ocupa la plaza de Bosnia y Herzegovina en el grupo B y se enfrenta a Canadá, Catar y Suiza.',
      'Todas las nacionalidades seleccionables pueden desbloquear ahora el Mundial secreto si cumplen los requisitos de carrera.',
    ],
  },
  {
    version: '0.8.0',
    date: '13 AGO 2026',
    title: 'Versión 0.8.0 · Seis caminos viables',
    notes: [
      'Remate colocado pasa a +3 OVR efectivo para que un solo consumible no domine los tres partidos de la fase de grupos.',
      'Pulsera biométrica y Brazalete se activan desde 75 de físico o moral; Plan personalizado reduce en 8 el siguiente coste después de descansar.',
      'Tercer pulmón entra por debajo de 35 de físico y las simulaciones de la ruta decisiva protegen la identidad de las seis familias de build.',
      'El Mundial usa sus 12 grupos y 48 selecciones de 2026: juegas contra los tres rivales reales de tu grupo y una selección no clasificada mantiene el Triplete.',
    ],
  },
  {
    version: '0.7.1',
    date: '13 AGO 2026',
    title: 'Versión 0.7.1 · Clasificar cuesta',
    notes: [
      'La fase de grupos simula tres partidos completos contra tres selecciones distintas.',
      'Dos o tres victorias clasifican para la primera eliminatoria; cero o una victoria elimina del Mundial.',
      'El marcador del bloque muestra las victorias y el objetivo de clasificación sin mezclarlo con puntos ni prestigio de club.',
    ],
  },
  {
    version: '0.7.0',
    date: '13 AGO 2026',
    title: 'Versión 0.7.0 · Dos finales, más builds',
    notes: [
      'El Triplete es ahora la final habitual. El Mundial sólo aparece como final secreta al llegar con OVR 85, prestigio 75 y un club de élite.',
      'La derrota en la fase de grupos no elimina: únicamente los dos siguientes partidos del camino al Mundial son eliminatorios.',
      'Cada victoria del tramo de liga alimenta Racha en su orden real, incluido el Ritual del vestuario; una derrota posterior corta la secuencia.',
      'Renovar con prestigio alto también concede +1 OVR, mientras el traspaso conserva su impulso inmediato a cambio de reiniciar prestigio y lealtad.',
      'Recuperación y Moral ganan nuevas utilidades en el ojeador, entre rondas decisivas y con moral alta. Resistencia reduce ahora 2 de coste por copia para acercarse al resto de builds.',
    ],
  },
  {
    version: '0.6.0',
    date: '13 AGO 2026',
    title: 'Versión 0.6.0 · Aprende jugando',
    notes: [
      'El tutorial principal aparece después de elegir tu primer equipo, para explicar OVR, físico, moral y prestigio dentro del contexto real de la carrera.',
      'La primera preparación de partido te enseña a seleccionar un consumible, conservarlo con «Sin consumible» y entender cuándo se gastará.',
      'La ayuda de cada nodo se cierra al soltar la pulsación, cancelar el gesto o retirar el ratón, sin activar por accidente una decisión.',
    ],
  },
  {
    version: '0.5.0',
    date: '13 AGO 2026',
    title: 'Versión 0.5.0 · Cada decisión cuenta',
    notes: [
      'La interfaz es más minimalista y responsive: objetos y decisiones ocupan menos en móvil y tablet, las filas conservan su alineación y la build y el registro pasan al menú superior.',
      'La primera visita incluye un tutorial breve sobre OVR, físico, moral y prestigio, además de explicar cuándo usar consumibles y cómo actúan las reliquias.',
      'Ya puedes entrar con Google sin mezclar la carrera local con la cuenta: al acceder, prevalece exclusivamente el progreso remoto y una cuenta nueva empieza desde cero.',
      'Las ofertas escalan con cada etapa para llevarte de clubes modestos a la élite, manteniendo una pequeña posibilidad de recibir antes una propuesta excepcional.',
      'Los tramos de liga muestran un reloj de 0′ a 90′ y actualizan el marcador en una animación compacta antes de resolver cada partido.',
      'Cada nodo explica su efecto al pasar el ratón, recibir foco o mediante una pulsación prolongada en móvil y tablet, sin activar por accidente una decisión.',
    ],
  },
  {
    version: '0.4.0',
    date: '12 AGO 2026',
    title: 'Versión 0.4.0 · La carrera toma forma',
    notes: [
      'Las cinco etapas tienen finales distintos: las primeras enseñan y dejan construir la build; las últimas exigen terminarla y afinarla.',
      'En una derrota de boss o derbi, la reacción puede aparecer entre el minuto 45 y el 80: acertarla convierte el marcador en una remontada; fallarla muestra el resultado final.',
      'La interfaz recorta texto repetido: la build, los condicionales, la preparación y la leyenda muestran primero la información que cambia tu decisión.',
      'Empezar en Celta Fortuna permite ascender al Celta de Vigo al terminar la primera temporada si eliges continuar en el club.',
    ],
  },
  {
    version: '0.3.0',
    date: '12 AGO 2026',
    title: 'Versión 0.3.0 · Builds que se entienden',
    notes: [
      'La build se compone únicamente de consumibles y reliquias: las sinergias aparecen automáticamente al reunir combinaciones de reliquias compatibles.',
      'El descanso ofrece tres descansos especializados: físico puro, moral pura o una recuperación equilibrada de menor potencia.',
      'Todas las decisiones muestran OVR, físico, moral y prestigio actuales; las recompensas priorizan sus efectos mecánicos y eliminan texto explicativo duplicado.',
      'El mapa gana separación vertical, conserva el origen de la ruta y aprovecha la altura completa del viewport en escritorio sin apelotonar los nodos.',
      'La navegación superior, el generador de nombre y las ilustraciones de recompensa reciben ajustes de alineación, foco y proporción para una lectura más limpia.',
    ],
  },
  {
    version: '0.2.0',
    date: '12 AGO 2026',
    title: 'Versión 0.2.0 · Builds con decisiones reales',
    notes: [
      'La preparación muestra el OVR efectivo y el físico y la moral proyectados; en móvil, consumibles, reliquias y condicionales se apilan con texto completo y legible.',
      'Las ofertas del ojeador equilibran reliquias afines, alternativas y consumibles; recargar ya no permite cambiar la oferta ni repetir el resultado de un encuentro pendiente.',
      'Las reacciones del final de etapa consumen físico o moral de verdad, y los consumibles de recuperación aplican su efecto además de gastarse.',
      'Las reliquias condicionales respetan umbrales monotónicos; las sinergias se forman exclusivamente al reunir combinaciones de reliquias.',
      'Pase entre líneas concede su bonificación de asistencia tanto a MCO como a DC y mejora frente a rivales agresivos sin quedar eclipsado por Remate colocado.',
    ],
  },
  {
    version: '0.1.4',
    date: '12 AGO 2026',
    title: 'Versión 0.1.4 · Un recorrido más fácil de leer',
    notes: [
      'El campo explica en todo momento cuál es la siguiente acción: elegir una casilla amarilla o disputar el final de etapa.',
      'Una clave visible diferencia la posición actual, las opciones disponibles y los caminos futuros mediante texto, forma y color.',
      'Los nodos muestran estados directos como «Elige», «Hecho» y «Estás aquí», y el recorrido señala claramente el inicio y la meta.',
    ],
  },
  {
    version: '0.1.3',
    date: '12 AGO 2026',
    title: 'Versión 0.1.3 · Cuenta y progreso entre dispositivos',
    notes: [
      'Ya puedes crear una cuenta o iniciar sesión para guardar automáticamente tu carrera y tus logros en la nube y continuar desde otro dispositivo.',
      'Cada cuenta sólo puede leer y modificar su propia partida, mientras el guardado local sigue disponible para jugar sin iniciar sesión.',
      'La pantalla de acceso incorpora «¿Has olvidado tu contraseña?» y envía un enlace seguro para establecer una nueva sin perder el progreso.',
      'Los enlaces de recuperación abren directamente el formulario de contraseña nueva y permiten solicitar otro enlace si el anterior ha caducado.',
    ],
  },
  {
    version: '0.1.2',
    date: '12 AGO 2026',
    title: 'Versión 0.1.2 · Pulido visual del mapa y diálogos',
    notes: [
      'Las conexiones del mapa terminan ahora en el centro exacto de cada nodo en filas de dos, tres o cuatro opciones y se mantienen alineadas en cualquier altura de pantalla.',
      'El icono y la sombra de las cabeceras de decisión se centran como una sola unidad cuando el título ocupa varias líneas en móvil.',
      'La insignia «Actual» del changelog conserva su composición horizontal y deja de partirse en pantallas estrechas.',
    ],
  },
  {
    version: '0.1.1',
    date: '12 AGO 2026',
    title: 'Versión 0.1.1 · Mejoras de experiencia móvil',
    notes: [
      'El campo aprovecha dinámicamente la altura disponible en móviles altos para separar mejor las rutas sin añadir scroll obligatorio.',
      'Leyenda y Build e informe comparten exactamente la misma altura, borde y sombra; abrir uno continúa cerrando el otro.',
      'El nombre del equipo se ajusta al espacio real entre el escudo y los controles para mostrarse siempre completo.',
      'Las alertas flotantes inferiores se eliminan; los acontecimientos permanecen disponibles en el historial de la carrera.',
      'Objetos, reliquias y decisiones de evento utilizan en móvil 49 pictogramas vectoriales propios y compactos en lugar de ilustraciones laterales.',
      'Las decisiones de cuatro opciones reciben una composición específica para evitar solapamientos entre títulos, riesgos y consecuencias.',
      'La ficha de una nueva run empieza sin nombre predeterminado y exige un nombre válido, rechazando también valores formados solo por espacios.',
    ],
  },
  {
    version: '0.1.0',
    date: '11 AGO 2026',
    title: 'Versión 0.1.0 · Primera versión jugable',
    notes: [
      'Carrera roguelike completa de cinco etapas, desde la cantera hasta el Mundial, con guardado local y migración segura de partidas anteriores.',
      'Interfaz totalmente responsive: OVR, físico, moral, prestigio y mapa permanecen visibles en móvil sin obligar a hacer scroll.',
      'Decisiones, ofertas y resultados adaptados a pantallas pequeñas, sin carruseles ni desplazamiento horizontal obligatorio.',
      'Contraste y legibilidad revisados en los temas claro y oscuro, con controles táctiles de al menos 44 píxeles.',
      'Animaciones de probabilidad y resultados más tensas, con suspense progresivo y alternativa respetuosa con movimiento reducido.',
      'Progresión de OVR sin topes artificiales por etapa: los tramos se reducen a tres partidos y conceden +1 con 0–1 victorias, +2 con dos y +3 al ganar los tres.',
      'Finales de etapa reajustadas para que físico, moral, consumibles y reliquias sigan importando incluso con un OVR alto.',
      'Consumibles y reliquias organizados en seis familias de build con sinergias de técnica, resistencia, vestuario, recuperación, supervivencia y racha.',
      'Eventos contextuales y fieles al mundo del fútbol, con rutas de riesgo bajo y decisiones de mayor riesgo y recompensa según etapa y club.',
      'Clubes, escudos, plantillas y derbis reales para las ligas disponibles; el prestigio en el club determina tus minutos de juego.',
      'Narración de los partidos renovada: cada acción muestra el escudo y los colores del equipo responsable, usando el color secundario rival cuando coincide.',
      'Los resultados de los tramos distinguen victorias en verde y derrotas en rojo mediante color, etiqueta textual y descripción accesible.',
      'El mapa móvil incorpora una leyenda desplegable que explica cada tipo de nodo sin reducir el espacio de juego.',
      'El recorrido de cada etapa se amplía a siete niveles con el patrón 2–3–4–3–4–3–2 antes del Boss, conexiones legibles y todas las ramas alcanzables.',
      'Palmarés permanente de 20 logros, incluidos siete retos temáticos de Real Madrid, Barcelona, Liverpool, Bayern Munich, Boca Juniors, Napoli y Athletic Bilbao.',
      'Accesibilidad mejorada con foco atrapado y restaurado en diálogos, estados anunciados y controles descritos para lectores de pantalla.',
    ],
  },
] as const

export function ChangelogDialog({ open, onClose }: ChangelogDialogProps) {
  const titleId = useId()
  const dialogRef = useRef<HTMLElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const frame = requestAnimationFrame(() => closeRef.current?.focus())
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>('button:not(:disabled),a[href]')]
      const first = focusable[0]
      const last = focusable.at(-1)
      if (!first || !last) return
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      cancelAnimationFrame(frame)
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
      opener?.focus()
    }
  }, [open])

  if (!open) return null

  return (
    <div className="dialog-backdrop changelog-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <section ref={dialogRef} className="changelog-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="changelog-dialog__header">
          <div><p className="eyebrow">Archivo del club · versión 0.13.0</p><h2 id={titleId}>Notas de versión</h2><p>Estás jugando a RoguelikeFC 0.13.0, con un Mundial separado por fases y escudos federativos locales.</p></div>
          <button ref={closeRef} className="changelog-dialog__close" type="button" onClick={onClose} aria-label="Cerrar notas de versión"><Icon icon="mdi:close" /></button>
        </header>
        <ol className="changelog-timeline">
          {CHANGELOG.map((release, index) => <li className={index === 0 ? 'is-current' : ''} key={release.version}>
            <div className="changelog-timeline__marker"><span>{release.version}</span><i /></div>
            <article>
              <header><div><p>{release.date}</p><h3>{release.title}</h3></div>{index === 0 && <strong>Actual</strong>}</header>
              <ul>{release.notes.map((note) => <li key={note}>{note}</li>)}</ul>
            </article>
          </li>)}
        </ol>
      </section>
    </div>
  )
}
